<script lang="ts" setup generic="T extends GenericObject = GenericObject">
import { computed, ref } from 'vue';
import {
  IconSortDescending,
  IconSortAscending,
  IconEyeOff,
  IconPinned,
  IconPinnedOff,
  IconCaretDownFilled,
  IconCaretUpFilled,
} from '@/icons';
import { ObActionList, ObActionListItem } from '../../action-list';
import { ObActionMenu } from '../../action-menu';
import { ObTooltip } from '../../tooltip';
import { useTableContext } from '../shared';
import type { GenericObject } from '../../../../shared/types';
import type { UiDataTableColumn } from '../interfaces';

interface Props {
  column?: UiDataTableColumn<T>;
}

defineOptions({ inheritAttrs: false });

const props = defineProps<Props>();

const {
  pinnedColumnsStartKeys,
  sorting,
  hideColumn,
  startDragging,
  draggedColumnIndex,
  draggedColumnKey,
  targetColumnIndex,
  targetColumnKey,
  setSorting,
  pinColumn,
  unpinColumn,
  columnsPinControl,
  columnsVisibilityControl,
  columnsReordering,
  resolveI18n,
} = useTableContext<T>();

const pinned = computed(
  () => props.column && pinnedColumnsStartKeys.value.includes(props.column.key),
);

function pin() {
  if (!props.column) {
    return;
  }

  pinColumn(props.column.key);
}

function unpin() {
  if (!props.column) {
    return;
  }

  unpinColumn(props.column.key);
}

function setAscendingSort() {
  if (!props.column) {
    return;
  }

  setSorting({
    sortBy: props.column.key,
    sortOrder: 'asc',
  });
}

function setDescendingSort() {
  if (!props.column) {
    return;
  }

  setSorting({
    sortBy: props.column.key,
    sortOrder: 'desc',
  });
}

let shouldResetSorting = false;

function toggleSorting() {
  if (!props.column) {
    return;
  }

  const { sortBy, sortOrder } = sorting.value;

  if (shouldResetSorting) {
    setSorting({
      sortBy: null,
      sortOrder: 'asc',
    });

    shouldResetSorting = false;

    return;
  }

  if (sortBy !== props.column.key) {
    setDescendingSort();
    return;
  }

  shouldResetSorting = true;

  if (sortOrder === 'asc') {
    setDescendingSort();
    return;
  }

  setAscendingSort();
}

const sortingActive = computed(() => props.column && sorting.value.sortBy === props.column.key);

const dropdownOpen = ref(false);

function onMouseDown(event: MouseEvent) {
  if (
    !columnsReordering.value ||
    !props.column ||
    props.column.frozenStart ||
    props.column.frozenEnd
  ) {
    return;
  }

  startDragging(event, props.column.key);
}

const hasMenu = computed(() => {
  return (
    !props.column?.frozenStart &&
    !props.column?.frozenEnd &&
    (props.column?.sortable || columnsPinControl.value || columnsVisibilityControl.value)
  );
});
</script>

<template>
  <ObTooltip
    placement="top"
    :activate-delay="200"
    :content="props.column?.tooltip"
    :disabled="!props.column?.tooltip"
  >
    <template #host="{ hostProps, hostRef }">
      <div
        v-bind="{ ...$attrs, ...hostProps }"
        :ref="hostRef"
        :class="[
          $style.root,
          {
            [$style.alignEnd]: props.column?.align === 'end',
            [$style.alignCenter]: props.column?.align === 'center',
            [$style.sortingActive]: sortingActive,
            [$style.dropdownOpen]: dropdownOpen,
          },
        ]"
        @mousedown.left="onMouseDown"
      >
        <div :class="$style.content">
          <slot />
        </div>
        <ObActionMenu v-if="hasMenu && props.column" v-model:open="dropdownOpen" overlay>
          <template #host="{ hostProps }">
            <button v-bind="hostProps" type="button" :class="$style.button" />
          </template>
          <ObActionList>
            <template v-if="props.column.sortable">
              <ObActionListItem @select="setAscendingSort()">
                <template #leadingVisual> <IconSortAscending /> </template>
                {{ resolveI18n('sortAscending') }}
              </ObActionListItem>
              <ObActionListItem @select="setDescendingSort()">
                <template #leadingVisual> <IconSortDescending /> </template>
                {{ resolveI18n('sortDescending') }}
              </ObActionListItem>
            </template>
            <template v-if="columnsPinControl">
              <ObActionListItem v-if="pinned" @select="unpin()">
                <template #leadingVisual> <IconPinnedOff /> </template>
                {{ resolveI18n('unpinColumn') }}
              </ObActionListItem>
              <ObActionListItem v-else @select="pin()">
                <template #leadingVisual> <IconPinned /> </template>
                {{ resolveI18n('pinColumn') }}
              </ObActionListItem>
            </template>
            <ObActionListItem
              v-if="columnsVisibilityControl"
              @select="hideColumn(props.column.key)"
            >
              <template #leadingVisual> <IconEyeOff /> </template>
              {{ resolveI18n('hideColumn') }}
            </ObActionListItem>
          </ObActionList>
        </ObActionMenu>
        <div v-if="props.column?.sortable" :class="$style.controls">
          <button type="button" :class="$style.sortingButton" @click="toggleSorting()">
            <IconCaretDownFilled v-if="!sortingActive || sorting.sortOrder === 'desc'" size="16" />
            <IconCaretUpFilled v-else size="16" />
          </button>
        </div>
      </div>
      <div
        v-if="
          props.column &&
          props.column.key === targetColumnKey &&
          props.column.key !== draggedColumnKey
        "
        :class="[
          $style.dropIndicator,
          {
            [$style.dropIndicatorStart]: draggedColumnIndex > targetColumnIndex,
          },
        ]"
      />
    </template>
  </ObTooltip>
</template>

<style lang="scss" module>
@use '../../../styles/shared';
@use '../shared' as tableSharedStyles;

.root {
  position: relative;
  display: flex;
  box-sizing: border-box;
  padding: 12px;
  font-size: 13px;
  line-height: 16px;
  width: 100%;
  align-items: center;
  background: #f5f5f5;
  font-weight: 500;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    border-right: 1px solid tableSharedStyles.$border-color;
    z-index: tableSharedStyles.$z-index-border;
    pointer-events: none;
  }

  &:hover,
  &.dropdownOpen {
    background: linear-gradient(0deg, rgba(#23395d, 0.05) 0%, rgba(#23395d, 0.05) 100%), #f5f5f5;
  }
}

.content {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.alignCenter {
  justify-content: center;
}

.alignEnd {
  justify-content: flex-end;
}

.button {
  @include shared.reset-button();
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  z-index: tableSharedStyles.$z-index-clickable-cel;

  &:focus-visible {
    box-shadow: inset 0 0 0 2px #3355ff;
  }
}

.button:active,
.dropdownOpen .button {
  box-shadow: inset 0 0 0 2px #7e57c2;
}

.controls {
  display: flex;
  position: relative;
  pointer-events: none;
  z-index: tableSharedStyles.$z-index-clickable-cel + 1;
}

.sortingButton {
  @include shared.reset-button();
  pointer-events: auto;
  width: 16px;
  height: 16px;
  color: #0e1a32;
  margin-left: 4px;
}

.root:not(.sortingActive) .sortingButton {
  color: #d2d3d9;
}

.dropIndicator {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background-color: #085cd9;
  pointer-events: none;
  z-index: tableSharedStyles.$z-index-drop-indicator;
}

.dropIndicatorStart {
  left: 0;
  right: auto;
}
</style>
